import type { LocalizedString } from '~/lib/experiences/i18n';
import type { Locale } from '~/lib/i18n';
import { DEFAULT_LOCALE } from '~/lib/i18n';

/** Every experience slug; each one needs a matching route file registered in `app/routes.ts`. */
export type ExperienceSlug = 'flow-field' | 'stone-face' | 'planet' | 'mechelen' | 'flag-wave';

/**
 * A single 3D experience in the showcase. Hand-curated in code on purpose: each entry is tied to
 * its own route file in `app/routes.ts`, so the list cannot drift through the CMS.
 */
export interface Experience {
  slug: ExperienceSlug;
  title: string;
  place: LocalizedString;
  summary: LocalizedString;
  techniques: string[];
  cover: { src: string; width: number; height: number; position: string };
  /** Short muted loop the showcase panel plays while in view, recorded with `scripts/record-experience-previews.mjs` */
  preview?: { webm: string; mp4: string };
  needsCamera?: boolean;
}

/** Showcase order: the first entry leads the page. */
export const EXPERIENCES: readonly Experience[] = [
  {
    slug: 'flag-wave',
    title: 'Flag Wave',
    place: { en: 'The drafting table', nl: 'De tekentafel' },
    summary: {
      en: 'A long pennant printed in the logo\'s two inks, flying from its pole and fraying into halftone dots that drift off on the wind. Hover it to stir up a gust.',
      nl: 'Een lange wimpel in de twee inkten van het logo, wapperend aan zijn mast en uitrafelend tot rasterpunten die met de wind meedrijven. Beweeg erover voor een windvlaag.',
    },
    techniques: ['Cloth shader', 'Halftone dissolve', 'Particles'],
    cover: { src: '/experiences/covers/flag-wave.webp', width: 1000, height: 1200, position: '45% 45%' },
    preview: { webm: '/experiences/previews/flag-wave.webm', mp4: '/experiences/previews/flag-wave.mp4' },
  },
  {
    slug: 'mechelen',
    title: 'Mechelen',
    place: { en: 'Hometown, Mechelen', nl: 'Hometown, Mechelen' },
    summary: {
      en: 'The old town inside the ring, every roof, facade and canal set as a two-ink print: red and cyan plates, halftone shadows and a register that never quite lines up.',
      nl: 'De binnenstad binnen de ring, elk dak, elke gevel en elke vaart gezet als tweekleurendruk: rode en cyaan platen, halftoonschaduwen en een register dat nooit helemaal klopt.',
    },
    techniques: ['Baked data maps', 'Halftone shader', 'Two-ink print'],
    cover: { src: '/experiences/covers/mechelen.webp', width: 1000, height: 1200, position: '50% 50%' },
    preview: { webm: '/experiences/previews/mechelen.webm', mp4: '/experiences/previews/mechelen.mp4' },
  },
  {
    slug: 'planet',
    title: 'Planet',
    place: { en: 'A world of your own', nl: 'Een wereld van jezelf' },
    summary: {
      en: 'A tiny planet that keeps itself in balance: rabbits graze and hide in the grass, foxes hunt the shore, birds flock the sky. Tune the water, colours and inhabitants live.',
      nl: 'Een kleine planeet die zichzelf in balans houdt: konijnen grazen en schuilen in het gras, vossen jagen aan de kust, vogels zwermen in de lucht. Stel het water, de kleuren en de bewoners live af.',
    },
    techniques: ['Procedural noise', 'Instancing', 'Ecosystem sim'],
    cover: { src: '/experiences/covers/planet.webp', width: 1000, height: 1326, position: '62% 50%' },
    preview: { webm: '/experiences/previews/planet.webm', mp4: '/experiences/previews/planet.mp4' },
  },
  {
    slug: 'flow-field',
    title: 'Flow Field',
    place: { en: 'Sint-Rombouts, Mechelen', nl: 'Sint-Rombouts, Mechelen' },
    summary: {
      en: 'A drone-scanned cathedral tower rebuilt from two million particles that drift, scatter and settle back into stone.',
      nl: 'Een met drones gescande kathedraaltoren, heropgebouwd uit twee miljoen deeltjes die afdrijven, uiteenspatten en terug tot steen bezinken.',
    },
    techniques: ['GPGPU', 'Point cloud', 'Flow field'],
    cover: { src: '/experiences/covers/flow-field.webp', width: 1000, height: 1200, position: '38% 30%' },
    preview: { webm: '/experiences/previews/flow-field.webm', mp4: '/experiences/previews/flow-field.mp4' },
  },
  {
    slug: 'stone-face',
    title: 'Stone Face',
    place: { en: 'Your webcam', nl: 'Jouw webcam' },
    summary: {
      en: 'A bust of tumbling stone shards that reads your face and mirrors every expression, live and entirely in your browser.',
      nl: 'Een buste van tuimelende stenen scherven die je gezicht leest en elke expressie live spiegelt, volledig in je browser.',
    },
    techniques: ['Face tracking', 'Instancing', 'Physics'],
    cover: { src: '/experiences/covers/stone-face.webp', width: 1100, height: 1500, position: '50% 45%' },
    needsCamera: true,
  },
];

/** The homepage block's selection: Stone Face asks for the camera, so it stays on the showcase. */
export const HOMEPAGE_EXPERIENCES: readonly Experience[] = EXPERIENCES.filter(experience => 'stone-face' !== experience.slug);

/**
 * @param slug - The experience to link
 * @returns Its locale-agnostic path, e.g. `/experiences/flow-field`, for the sitemap and absolute URLs
 */
export function experienceRoute(slug: ExperienceSlug): string {
  return `/experiences/${slug}`;
}

/**
 * @param locale - Locale whose copy to link; the default locale has no path segment
 * @returns The site-relative path of the experiences showcase, e.g. `/nl/experiences`
 */
export function showcasePath(locale: Locale = DEFAULT_LOCALE): string {
  return DEFAULT_LOCALE === locale ? '/experiences' : `/${locale}/experiences`;
}

/**
 * @param slug - The experience to link
 * @param locale - Locale whose copy to link; the default locale has no path segment
 * @returns The site-relative path of that experience, e.g. `/nl/experiences/flow-field`
 */
export function experiencePath(slug: ExperienceSlug, locale: Locale = DEFAULT_LOCALE): string {
  const prefix = DEFAULT_LOCALE === locale ? '' : `/${locale}`;
  return `${prefix}${experienceRoute(slug)}`;
}

/**
 * @param slug - The experience whose stage to name
 * @returns The shared view-transition name linking its showcase panel to its full-screen page
 */
export function experienceTransitionName(slug: ExperienceSlug): string {
  return `experience-${slug}`;
}
